import React, { useEffect, useMemo, useState } from 'react';
import { Menu, Layout, Modal, Button, Form, Input, notification, Switch } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { LockOutlined, MailOutlined, LoginOutlined, CheckCircleFilled } from '@ant-design/icons';
import axios from '../axiosConfig';
import ProfileModal from '../ProfileModal';
import '../style.css';

const { Header: AntHeader } = Layout;

const displayUserLabel = (user) => {
  if (!user) return '';
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.username || '';
};

const Header = ({ loggedInUser, setLoggedInUser }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [isRegisterModalVisible, setIsRegisterModalVisible] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [supportAvailable, setSupportAvailable] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const canToggleSupport = useMemo(() => {
    if (!loggedInUser) return false;
    if (loggedInUser.role === 'SUPERUSER') return true;
    const mail = String(loggedInUser.email || loggedInUser.username || '').toLowerCase();
    return mail.includes('@datatek.sk');
  }, [loggedInUser]);

  const selectedMenuKey = useMemo(() => {
    if (location.pathname.startsWith('/dashboard')) {
      return 'dashboard';
    }
    if (location.pathname.startsWith('/projects')) {
      return 'projects';
    }
    if (location.pathname.startsWith('/systems')) {
      return 'systems';
    }
    if (location.pathname.startsWith('/kpi')) {
      return 'kpi';
    }
    return 'incidents';
  }, [location.pathname]);

  const fetchSupportStatus = () => {
    axios.get('/support-status')
      .then(r => setSupportAvailable(!!r.data?.available))
      .catch(() => {});
  };

  useEffect(() => {
    if (!loggedInUser) return;
    fetchSupportStatus();
    const timer = setInterval(fetchSupportStatus, 30000);
    return () => clearInterval(timer);
  }, [loggedInUser]);

  const handleSupportToggle = (checked) => {
    axios.post('/support-status', { available: checked })
      .then(r => {
        setSupportAvailable(!!r.data?.available);
        notification.success({
          message: checked
            ? 'Technická podpora je teraz k dispozícii.'
            : 'Technická podpora je vypnutá.',
        });
      })
      .catch(() => notification.error({ message: 'Nepodarilo sa zmeniť stav podpory.' }));
  };

  const showLoginModal = () => {
    setIsRegisterModalVisible(false);
    setRegisterSuccess(false);
    setIsModalVisible(true);
  };

  const showRegisterModal = () => {
    setIsModalVisible(false);
    setRegisterSuccess(false);
    setIsRegisterModalVisible(true);
  };

  useEffect(() => {
    const onOpenLogin = () => showLoginModal();
    const onOpenRegister = () => showRegisterModal();
    window.addEventListener('open-login-modal', onOpenLogin);
    window.addEventListener('open-register-modal', onOpenRegister);
    return () => {
      window.removeEventListener('open-login-modal', onOpenLogin);
      window.removeEventListener('open-register-modal', onOpenRegister);
    };
  }, []);

  const handleCancel = () => {
    if (loginLoading) return;
    setIsModalVisible(false);
  };

  const openRegisterModal = () => {
    showRegisterModal();
  };

  const handleRegisterCancel = () => {
    if (registerLoading) return;
    setIsRegisterModalVisible(false);
    setRegisterSuccess(false);
  };

  const backToLogin = () => {
    setIsRegisterModalVisible(false);
    setRegisterSuccess(false);
    setIsModalVisible(true);
  };

  const handleLogin = async (values) => {
    setLoginLoading(true);
    try {
      const response = await axios.post('/login', values);
      if (response.data && response.data.username) {
        setLoggedInUser(response.data);
      } else {
        console.error('Login response does not contain username:', response.data);
      }
      setIsModalVisible(false);
      notification.success({
        message: 'Prihlásenie úspešné.'
      });
    } catch (error) {
      console.error('Login failed:', error);
      notification.error({
        message: 'Chybný e-mail alebo heslo.'
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (values) => {
    setRegisterLoading(true);
    try {
      await axios.post('/register-request', { email: values.email });
      setRegisterSuccess(true);
    } catch (error) {
      console.error('Registration request failed:', error);
      notification.error({
        message: 'Žiadosť sa nepodarilo odoslať. Skúste to prosím znova.',
      });
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/logout');
      setLoggedInUser(null);
      setProfileOpen(false);
      notification.success({
        message: 'Odhlásenie úspešné.'
      });
    } catch (error) {
      console.error('Logout failed:', error);
      notification.error({
        message: 'Odhlásenie neúspešné.',
      });
    }
  };

  return (
    <AntHeader>
      <div className="header-content">
        <div className="logo">
          <Link to="/">
            <img src="/logo.png" height="35px" alt="logo" />
            <span className="logo-text">REQUEST MANAGER</span>
          </Link>
        </div>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={[selectedMenuKey]}
          className="header-nav"
          style={{ flex: 1, minWidth: 0 }}
        >
          <Menu.Item key="incidents">
            <Link to="/">Požiadavky</Link>
          </Menu.Item>
          <Menu.Item key="projects">
            <Link to="/projects">Projekty</Link>
          </Menu.Item>
          <Menu.Item key="systems">
            <Link to="/systems">Systémy</Link>
          </Menu.Item>
          <Menu.Item key="kpi">
            <Link to="/kpi">
              <span className="nav-label-full">KPI / SLA</span>
              <span className="nav-label-short">KPI</span>
            </Link>
          </Menu.Item>
          <Menu.Item key="dashboard">
            <Link to="/dashboard">
              <span className="nav-label-full">Kalendár udalostí</span>
              <span className="nav-label-short">Kalendár</span>
            </Link>
          </Menu.Item>
        </Menu>
        <div className="header-actions">
          {loggedInUser && (
            <div className={`inc-support-badge header-support ${supportAvailable ? 'online' : 'offline'}`}>
              <span className={`inc-support-dot ${supportAvailable ? 'pulse' : ''}`} />
              <span className="inc-support-text">
                {supportAvailable
                  ? 'Technická podpora je k dispozícii'
                  : 'Technická podpora nie je k dispozícii'}
              </span>
              {canToggleSupport && (
                <Switch
                  size="small"
                  checked={supportAvailable}
                  onChange={handleSupportToggle}
                  title={supportAvailable ? 'Vypnúť podporu' : 'Zapnúť podporu'}
                />
              )}
            </div>
          )}
          {loggedInUser && loggedInUser.username ? (
            <div className="header-user-block">
              <button
                type="button"
                className="header-user-trigger"
                onClick={() => setProfileOpen(true)}
                title="Môj profil"
              >
                {displayUserLabel(loggedInUser)}
              </button>
              <button
                type="button"
                className="header-logout-link"
                onClick={handleLogout}
              >
                Odhlásiť
              </button>
            </div>
          ) : (
            <Button type="primary" onClick={showLoginModal}>Prihlásiť</Button>
          )}
        </div>
      </div>

      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        loggedInUser={loggedInUser}
        setLoggedInUser={setLoggedInUser}
      />

      <Modal
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        centered
        width={420}
        className="login-modal"
        closable={!loginLoading}
        maskClosable={!loginLoading}
        destroyOnClose
      >
        <div className="login-modal-inner">
          <div className="login-modal-brand">
            <img src="/logo.png" alt="datatek" className="login-modal-logo" />
            <div className="login-modal-brand-text">
              <span className="login-modal-product">REQUEST MANAGER</span>
              <span className="login-modal-company">datatek, spol. s r.o.</span>
            </div>
          </div>

          <h2 className="login-modal-title">Prihlásenie</h2>
          <p className="login-modal-sub">Zadajte firemný e-mail a heslo pre prístup k požiadavkám.</p>

          <Form
            layout="vertical"
            onFinish={handleLogin}
            requiredMark={false}
            className="login-modal-form"
          >
            <Form.Item
              name="username"
              label="E-mail"
              rules={[
                { required: true, message: 'Zadajte e-mail' },
                { type: 'email', message: 'Neplatný formát e-mailu' },
              ]}
            >
              <Input
                size="large"
                prefix={<MailOutlined />}
                placeholder="meno@firma.sk"
                autoComplete="username"
                disabled={loginLoading}
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="Heslo"
              rules={[{ required: true, message: 'Zadajte heslo' }]}
            >
              <Input.Password
                size="large"
                prefix={<LockOutlined />}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={loginLoading}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loginLoading}
                icon={<LoginOutlined />}
                className="login-modal-submit"
              >
                Prihlásiť sa
              </Button>
            </Form.Item>
          </Form>

          <p className="login-modal-footer">
            Nemáte účet?{' '}
            <button type="button" className="login-modal-link" onClick={openRegisterModal}>
              Registrácia
            </button>
          </p>
        </div>
      </Modal>

      <Modal
        open={isRegisterModalVisible}
        onCancel={handleRegisterCancel}
        footer={null}
        centered
        width={420}
        className="login-modal"
        closable={!registerLoading}
        maskClosable={!registerLoading}
        destroyOnClose
      >
        <div className="login-modal-inner">
          <div className="login-modal-brand">
            <img src="/logo.png" alt="datatek" className="login-modal-logo" />
            <div className="login-modal-brand-text">
              <span className="login-modal-product">REQUEST MANAGER</span>
              <span className="login-modal-company">datatek, spol. s r.o.</span>
            </div>
          </div>

          {registerSuccess ? (
            <div className="login-modal-success">
              <CheckCircleFilled className="login-modal-success-icon" />
              <h2 className="login-modal-title">Ďakujeme za registráciu</h2>
              <p className="login-modal-sub">
                Vašu žiadosť sme prijali. Po schválení vás budeme informovať
                na zadanej e-mailovej adrese.
              </p>
              <Button
                type="primary"
                size="large"
                block
                className="login-modal-submit"
                onClick={handleRegisterCancel}
              >
                Zavrieť
              </Button>
            </div>
          ) : (
            <>
              <h2 className="login-modal-title">Registrácia</h2>
              <p className="login-modal-sub">
                Zadajte e-mail, na ktorý vám pošleme prístupové údaje po schválení.
              </p>

              <Form
                layout="vertical"
                onFinish={handleRegister}
                requiredMark={false}
                className="login-modal-form"
              >
                <Form.Item
                  name="email"
                  label="E-mail"
                  rules={[
                    { required: true, message: 'Zadajte e-mail' },
                    { type: 'email', message: 'Neplatný formát e-mailu' },
                  ]}
                >
                  <Input
                    size="large"
                    prefix={<MailOutlined />}
                    placeholder="meno@firma.sk"
                    autoComplete="email"
                    disabled={registerLoading}
                  />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={registerLoading}
                    className="login-modal-submit"
                  >
                    Odoslať žiadosť
                  </Button>
                </Form.Item>
              </Form>

              <p className="login-modal-footer">
                Už máte účet?{' '}
                <button type="button" className="login-modal-link" onClick={backToLogin}>
                  Prihlásiť sa
                </button>
              </p>
            </>
          )}
        </div>
      </Modal>
    </AntHeader>
  );
};

export default Header;
